import * as React from "react";
import {
  AlertCircle,
  CheckCircle2,
  Download,
  File,
  FileClock,
  Globe,
  Images,
  Info,
  Loader2,
  Image as ImageIcon,
  Minus,
  Plus,
  RefreshCw,
  Trash2,
  Upload,
  X,
  type LucideIcon,
} from "lucide-react";
import { JsonView, darkStyles } from "react-json-view-lite";
import "react-json-view-lite/dist/index.css";
import { Modal } from "../../../ui/components/Modal";
import { Button } from "../../../ui/components/Button";
import { Text } from "../../../ui/components/Text";
import { Callout } from "../../../ui/components/Callout";
import { CollapsibleSection } from "../../../ui/components/CollapsibleSection";
import { ListView } from "../../../ui/components/ListView";
import {
  MediaUploader,
  type MediaEntry,
} from "../../../ui/components/MediaUploader";
import { renderMediaTypeBadge } from "./mediaTypeBadge";
import type { ColumnDefinition } from "../../../ui/components/Table/types";
import type { ListParams, ListResult } from "../../../ui/api/listApi.types";
import { vars } from "../../../ui/styles/theme.css";
import {
  applyImport,
  previewImport,
  waitForJob,
} from "../../lib/api/data-transfer";
import type {
  ChangeKind,
  ChangeSetItem,
  DataJob,
  DataTransferEntity,
  ImportPreviewResult,
} from "../../lib/api/data-transfer.types";
import { getAuthCookie } from "../../lib/auth/session-cookie";
import { useAuth } from "../../lib/auth/hooks";
import { useI18n } from "../../lib/i18n";
import {
  getYoutubeThumbnailUrl,
  getYoutubeVideoId,
  isYoutubeUrl,
} from "../../lib/media/youtube";

export interface ImportModalProps {
  open: boolean;
  onClose: () => void;
  expectedEntity?: DataTransferEntity;
  entityLabel: string;
  onApplied?: () => void;
}

type ChangeRow = ChangeSetItem & {
  imageUrl?: string | null;
};

type ImportPhase =
  | "idle"
  | "parsing"
  | "starting"
  | "pending"
  | "running"
  | "done"
  | "error";

type ProgressCopy = {
  title: string;
  subtitle?: string;
  Icon: LucideIcon;
  iconColor: string;
  spin?: boolean;
};

const KIND_LABEL: Record<ChangeKind, string> = {
  added: "addition",
  updated: "update",
  deleted: "deletion",
  unchanged: "unchanged",
};

const KIND_COLOR: Record<ChangeKind, string> = {
  added: "#2e7d32",
  updated: "#1565c0",
  deleted: "#c62828",
  unchanged: "#6b7280",
};

const SUMMARY_CHIPS: Array<{
  kind: ChangeKind;
  key: "added" | "updated" | "deleted" | "unchanged";
  singular: string;
  plural: string;
  color: string;
  bg: string;
  border: string;
  Icon: LucideIcon;
}> = [
  {
    kind: "added",
    key: "added",
    singular: "addition",
    plural: "additions",
    color: "#166534",
    bg: "#F0FDF4",
    border: "#BBF7D0",
    Icon: Plus,
  },
  {
    kind: "updated",
    key: "updated",
    singular: "update",
    plural: "updates",
    color: "#1D4ED8",
    bg: "#EFF6FF",
    border: "#BFDBFE",
    Icon: RefreshCw,
  },
  {
    kind: "deleted",
    key: "deleted",
    singular: "deletion",
    plural: "deletions",
    color: "#B91C1C",
    bg: "#FEF2F2",
    border: "#FECACA",
    Icon: Trash2,
  },
  {
    kind: "unchanged",
    key: "unchanged",
    singular: "unchanged",
    plural: "unchanged",
    color: "#4B5563",
    bg: "#F3F4F6",
    border: "#E5E7EB",
    Icon: Minus,
  },
];

const jsonPreviewShellStyle: React.CSSProperties = {
  borderRadius: 8,
  padding: "12px 16px",
  fontSize: 12,
  lineHeight: 1.6,
  overflowX: "auto",
  fontFamily: "monospace",
  maxHeight: 280,
  overflowY: "auto",
  background: vars.color.muted,
  border: `1px solid ${vars.color.border}`,
};

function mimeFromMediaKey(key: string): string {
  const lower = key.toLowerCase();
  if (lower.endsWith(".png")) return "image/png";
  if (lower.endsWith(".jpg") || lower.endsWith(".jpeg")) return "image/jpeg";
  if (lower.endsWith(".gif")) return "image/gif";
  if (lower.endsWith(".webp")) return "image/webp";
  if (lower.endsWith(".svg")) return "image/svg+xml";
  if (lower.endsWith(".mp4")) return "video/mp4";
  if (lower.endsWith(".webm")) return "video/webm";
  if (lower.endsWith(".mov")) return "video/quicktime";
  return "application/octet-stream";
}

const CONTENT_KEY_PREFIX = "certification.content.";

type CertModuleLike = {
  slug: string;
  heroMediaKey?: string | null;
  heroImageUrl?: string | null;
  trainings?: Array<{
    stepNumber?: number;
    title?: string | null;
    section?: string | null;
    transcript?: string | null;
    videoUrl?: string | null;
    thumbnailUrl?: string | null;
    videoMediaKey?: string | null;
    thumbnailMediaKey?: string | null;
    questions?: Array<{ text?: string | null; choices?: string[] }>;
  }>;
};

function isContentLocKey(value: string | null | undefined): value is string {
  return typeof value === "string" && value.startsWith(CONTENT_KEY_PREFIX);
}

/** Source modules for selected non-delete changes (resolved from the import envelope). */
function selectedCertModules(
  preview: ImportPreviewResult,
  selectedIds: string[],
): CertModuleLike[] {
  const selected = new Set(selectedIds);
  const changes = preview.changeset.items.filter(
    (c) => selected.has(c.id) && c.kind !== "deleted",
  );
  const bySlug = new Map(
    (preview.items as CertModuleLike[]).map((item) => [item.slug, item]),
  );
  const out: CertModuleLike[] = [];
  for (const change of changes) {
    const item = bySlug.get(change.matchKey);
    if (item) out.push(item);
  }
  return out;
}

function collectMediaKeysFromCertModules(modules: CertModuleLike[]): string[] {
  const keys = new Set<string>();
  for (const mod of modules) {
    if (mod.heroMediaKey) keys.add(mod.heroMediaKey);
    for (const t of mod.trainings ?? []) {
      // YouTube stays a hotlink — never treat its watch URL as ZIP binary media.
      if (t.videoMediaKey && !isYoutubeUrl(t.videoUrl)) keys.add(t.videoMediaKey);
      if (t.thumbnailMediaKey) keys.add(t.thumbnailMediaKey);
    }
  }
  return [...keys];
}

function collectContentKeysFromCertModules(modules: CertModuleLike[]): string[] {
  const keys = new Set<string>();
  const add = (value: string | null | undefined) => {
    if (isContentLocKey(value)) keys.add(value);
  };
  for (const mod of modules) {
    for (const t of mod.trainings ?? []) {
      add(t.title);
      add(t.section);
      add(t.transcript);
      for (const q of t.questions ?? []) {
        add(q.text);
        for (const choice of q.choices ?? []) add(choice);
      }
    }
  }
  return [...keys];
}

type LanguageLike = {
  code: string;
  name?: string;
  flagUrl?: string | null;
  flagMediaKey?: string | null;
  modules?: Array<{
    context: string;
    definitions?: Record<string, string>;
  }>;
};

/** Source language packs for selected non-delete changes. */
function selectedLanguages(
  preview: ImportPreviewResult,
  selectedIds: string[],
): LanguageLike[] {
  const selected = new Set(selectedIds);
  const changes = preview.changeset.items.filter(
    (c) => selected.has(c.id) && c.kind !== "deleted",
  );
  const byCode = new Map(
    (preview.items as LanguageLike[]).map((item) => [
      String(item.code ?? "").toLowerCase(),
      item,
    ]),
  );
  const out: LanguageLike[] = [];
  for (const change of changes) {
    const fromItems = byCode.get(change.matchKey.toLowerCase());
    const after = change.after as LanguageLike | undefined;
    const item = fromItems ?? after;
    if (item?.code) out.push(item);
  }
  return out;
}

function collectMediaKeysFromLanguages(langs: LanguageLike[]): string[] {
  const keys = new Set<string>();
  for (const lang of langs) {
    if (lang.flagMediaKey) keys.add(lang.flagMediaKey);
  }
  return [...keys];
}

/** Resolve ZIP bytes under the requested key, including basename/media/ aliases. */
function resolveMediaBase64Value(
  mediaBase64: Record<string, string>,
  key: string,
): string | undefined {
  if (mediaBase64[key] != null) return mediaBase64[key];
  const base = key.includes("/") ? key.slice(key.lastIndexOf("/") + 1) : key;
  const candidates = [
    key.startsWith("media/") ? key : `media/${base}`,
    `media/${base}`,
    base,
  ];
  for (const c of candidates) {
    if (mediaBase64[c] != null) return mediaBase64[c];
  }
  return undefined;
}

function sliceMediaBase64(
  mediaBase64: Record<string, string> | undefined,
  keys: string[],
): Record<string, string> {
  const out: Record<string, string> = {};
  if (!mediaBase64) return out;
  for (const key of keys) {
    const value = resolveMediaBase64Value(mediaBase64, key);
    if (value != null) out[key] = value;
  }
  return out;
}

type DefinitionDiffBucket = {
  added: Record<string, string>;
  changed: Record<string, { before: string; after: string }>;
  removed: Record<string, string>;
};

function diffDefinitionMaps(
  before: Record<string, string>,
  after: Record<string, string>,
): DefinitionDiffBucket {
  const added: Record<string, string> = {};
  const changed: Record<string, { before: string; after: string }> = {};
  const removed: Record<string, string> = {};
  const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
  for (const key of keys) {
    const b = before[key];
    const a = after[key];
    if (b == null && a != null) added[key] = a;
    else if (b != null && a == null) removed[key] = b;
    else if (b != null && a != null && b !== a) changed[key] = { before: b, after: a };
  }
  return { added, changed, removed };
}

function modulesToDefsByContext(
  modules: LanguageLike["modules"] | undefined,
): Record<string, Record<string, string>> {
  const out: Record<string, Record<string, string>> = {};
  for (const mod of modules ?? []) {
    out[mod.context] = { ...(mod.definitions ?? {}) };
  }
  return out;
}

function countDiffBucket(bucket: DefinitionDiffBucket): number {
  return (
    Object.keys(bucket.added).length +
    Object.keys(bucket.changed).length +
    Object.keys(bucket.removed).length
  );
}

/**
 * Preview of definition key changes for selected language packs.
 * Added languages show full modules; updates show per-context diffs.
 */
function buildLanguageDefinitionsPreview(
  preview: ImportPreviewResult,
  selectedIds: string[],
): {
  languages: Record<
    string,
    {
      kind: string;
      name: string;
      summary: {
        added: number;
        changed: number;
        removed: number;
        totalKeys: number;
      };
      byContext: Record<
        string,
        DefinitionDiffBucket | { definitions: Record<string, string> }
      >;
    }
  >;
} | null {
  const selected = new Set(selectedIds);
  const languages: Record<
    string,
    {
      kind: string;
      name: string;
      summary: {
        added: number;
        changed: number;
        removed: number;
        totalKeys: number;
      };
      byContext: Record<
        string,
        DefinitionDiffBucket | { definitions: Record<string, string> }
      >;
    }
  > = {};

  for (const change of preview.changeset.items) {
    if (!selected.has(change.id) || change.kind === "unchanged") continue;
    if (change.kind === "deleted") continue;

    const after = (change.after ?? {}) as LanguageLike;
    const before = (change.before ?? {}) as LanguageLike;
    const code = (after.code || change.matchKey).toLowerCase();
    const afterByCtx = modulesToDefsByContext(after.modules);
    const beforeByCtx = modulesToDefsByContext(before.modules);
    const contexts = new Set([
      ...Object.keys(afterByCtx),
      ...Object.keys(beforeByCtx),
    ]);

    const byContext: Record<
      string,
      DefinitionDiffBucket | { definitions: Record<string, string> }
    > = {};
    let added = 0;
    let changed = 0;
    let removed = 0;
    let totalKeys = 0;

    if (change.kind === "added") {
      for (const ctx of contexts) {
        const defs = afterByCtx[ctx] ?? {};
        const n = Object.keys(defs).length;
        if (!n) continue;
        byContext[ctx] = { definitions: defs };
        added += n;
        totalKeys += n;
      }
    } else {
      for (const ctx of contexts) {
        const bucket = diffDefinitionMaps(
          beforeByCtx[ctx] ?? {},
          afterByCtx[ctx] ?? {},
        );
        const n = countDiffBucket(bucket);
        if (!n) continue;
        byContext[ctx] = bucket;
        added += Object.keys(bucket.added).length;
        changed += Object.keys(bucket.changed).length;
        removed += Object.keys(bucket.removed).length;
        totalKeys += n;
      }
    }

    if (Object.keys(byContext).length === 0) continue;

    languages[code] = {
      kind: change.kind,
      name: after.name || change.label || code,
      summary: { added, changed, removed, totalKeys },
      byContext,
    };
  }

  if (Object.keys(languages).length === 0) return null;
  return { languages };
}

function sliceLocalizationForKeys(
  localization: ImportPreviewResult["localization"],
  keys: string[],
): ImportPreviewResult["localization"] {
  if (!localization || !keys.length) return null;
  const keySet = new Set(keys);
  const definitionsByLang: Record<string, Record<string, string>> = {};
  for (const [lang, defs] of Object.entries(localization.definitionsByLang ?? {})) {
    const sliced: Record<string, string> = {};
    for (const key of keySet) {
      const value = defs[key];
      if (value != null && value !== "") sliced[key] = value;
    }
    if (Object.keys(sliced).length > 0) definitionsByLang[lang] = sliced;
  }
  if (Object.keys(definitionsByLang).length === 0) return null;
  return {
    module: localization.module,
    defaultLanguage: localization.defaultLanguage,
    definitionsByLang,
  };
}

function dataUrlFromMediaKey(
  mediaBase64: Record<string, string>,
  key: string | null | undefined,
): string | null {
  if (!key || mediaBase64[key] == null) return null;
  const mime = mimeFromMediaKey(key);
  return `data:${mime};base64,${mediaBase64[key]}`;
}

/**
 * Build gallery tiles for selected import modules.
 * Hosted media uses ZIP base64 when present; YouTube uses the watch URL so
 * MediaUploader can resolve the real poster.
 */
function mediaEntriesFromSelectedModules(
  modules: CertModuleLike[],
  mediaBase64: Record<string, string>,
): MediaEntry[] {
  const seen = new Set<string>();
  const entries: MediaEntry[] = [];
  const push = (id: string, url: string, previewUrl?: string | null) => {
    if (!url || seen.has(id) || seen.has(url)) return;
    seen.add(id);
    seen.add(url);
    entries.push({
      id,
      url,
      ...(previewUrl ? { previewUrl } : null),
    });
  };

  for (const mod of modules) {
    const heroData = dataUrlFromMediaKey(mediaBase64, mod.heroMediaKey);
    if (heroData) push(`hero-${mod.slug}`, heroData, heroData);
    else if (mod.heroImageUrl) {
      push(`hero-${mod.slug}`, mod.heroImageUrl, mod.heroImageUrl);
    }

    for (const t of mod.trainings ?? []) {
      const step = t.stepNumber ?? 0;
      const thumbData = dataUrlFromMediaKey(mediaBase64, t.thumbnailMediaKey);
      const thumbUrl = thumbData ?? t.thumbnailUrl ?? null;

      if (t.videoUrl && isYoutubeUrl(t.videoUrl)) {
        const ytId = getYoutubeVideoId(t.videoUrl);
        push(
          `video-${mod.slug}-${step}`,
          t.videoUrl,
          thumbUrl ?? (ytId ? getYoutubeThumbnailUrl(ytId) : null),
        );
        continue;
      }

      const videoData = dataUrlFromMediaKey(mediaBase64, t.videoMediaKey);
      if (videoData) {
        push(`video-${mod.slug}-${step}`, videoData, thumbUrl);
      } else if (t.videoUrl) {
        push(`video-${mod.slug}-${step}`, t.videoUrl, thumbUrl);
      } else if (thumbUrl) {
        push(`thumb-${mod.slug}-${step}`, thumbUrl, thumbUrl);
      }
    }
  }
  return entries;
}

function progressCopy(
  phase: ImportPhase,
  errorMessage: string | null,
): ProgressCopy | null {
  switch (phase) {
    case "parsing":
      return {
        title: "Parsing file…",
        subtitle: "Building a preview of what will change.",
        Icon: Loader2,
        iconColor: vars.color.info,
        spin: true,
      };
    case "starting":
      return {
        title: "Starting import…",
        subtitle: "Creating an import job. A checkpoint will be created first.",
        Icon: Loader2,
        iconColor: vars.color.info,
        spin: true,
      };
    case "pending":
      return {
        title: "Queued…",
        subtitle: "Waiting for the import job to start.",
        Icon: FileClock,
        iconColor: vars.color.info,
      };
    case "running":
      return {
        title: "Applying changes…",
        subtitle:
          "Creating a checkpoint, then applying selected changes. You can close this dialog.",
        Icon: FileClock,
        iconColor: vars.color.info,
      };
    case "done":
      return {
        title: "Import complete",
        subtitle: "Selected changes have been applied.",
        Icon: CheckCircle2,
        iconColor: vars.color.success,
      };
    case "error":
      return {
        title: "Import failed",
        subtitle: errorMessage || "Something went wrong. Please try again.",
        Icon: AlertCircle,
        iconColor: vars.color.danger,
      };
    default:
      return null;
  }
}

function phaseFromJobStatus(status: DataJob["status"]): ImportPhase | null {
  if (status === "pending") return "pending";
  if (status === "running") return "running";
  if (status === "ready") return "done";
  if (status === "failed") return "error";
  return null;
}

function ChangeSummaryChips({
  summary,
}: {
  summary: {
    added: number;
    updated: number;
    deleted: number;
    unchanged?: number;
  };
}) {
  const chips = SUMMARY_CHIPS.filter((c) => (summary[c.key] ?? 0) > 0);
  if (!chips.length) {
    return (
      <Text level="body-small" color="muted" style={{ textAlign: "center" }}>
        No pending changes
      </Text>
    );
  }
  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "center",
        gap: 8,
      }}
    >
      {chips.map(({ key, singular, plural, color, bg, border, Icon }) => {
        const n = summary[key] ?? 0;
        return (
          <span
            key={key}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "4px 10px",
              borderRadius: 9999,
              background: bg,
              border: `1px solid ${border}`,
              color,
              fontSize: 12,
              fontWeight: 600,
              lineHeight: 1.3,
              whiteSpace: "nowrap",
            }}
          >
            <Icon size={13} strokeWidth={2.25} />
            {n} {n === 1 ? singular : plural}
          </span>
        );
      })}
    </div>
  );
}

const thumbCellStyle: React.CSSProperties = {
  width: 40,
  height: 40,
  borderRadius: 6,
  overflow: "hidden",
  background: vars.color.muted,
  border: `1px solid ${vars.color.border}`,
  flexShrink: 0,
  position: "relative",
};

const thumbPlaceholderStyle: React.CSSProperties = {
  ...thumbCellStyle,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

function lookupMediaDataUrl(
  mediaBase64: Record<string, string> | undefined,
  key: string | null | undefined,
): string | null {
  if (!key || !mediaBase64) return null;
  const direct = dataUrlFromMediaKey(mediaBase64, key);
  if (direct) return direct;
  // ZIP parse indexes both full paths and media/<basename>.
  const base = key.includes("/") ? key.slice(key.lastIndexOf("/") + 1) : key;
  const prefixed = key.startsWith("media/") ? key : `media/${base}`;
  return (
    dataUrlFromMediaKey(mediaBase64, prefixed) ??
    dataUrlFromMediaKey(mediaBase64, `media/${base}`) ??
    dataUrlFromMediaKey(mediaBase64, base)
  );
}

/** Flag tiles for selected language packs (ZIP bytes preferred, then flagUrl). */
function mediaEntriesFromSelectedLanguages(
  langs: LanguageLike[],
  mediaBase64: Record<string, string>,
): MediaEntry[] {
  const seen = new Set<string>();
  const entries: MediaEntry[] = [];
  for (const lang of langs) {
    const fromZip = lookupMediaDataUrl(mediaBase64, lang.flagMediaKey);
    const url = fromZip ?? lang.flagUrl ?? null;
    if (!url || seen.has(url)) continue;
    seen.add(url);
    entries.push({
      id: `flag-${lang.code}`,
      url,
      previewUrl: url,
    });
  }
  return entries;
}

function resolveChangeImageUrl(
  preview: ImportPreviewResult,
  change: ChangeSetItem,
): string | null {
  if (preview.entity === "certification-modules") {
    const fromItems = (preview.items as Array<{
      slug?: string;
      heroImageUrl?: string | null;
      heroMediaKey?: string | null;
      trainings?: Array<{
        thumbnailUrl?: string | null;
        thumbnailMediaKey?: string | null;
        videoUrl?: string | null;
      }>;
    }>).find((item) => item.slug === change.matchKey);

    const after = change.after as
      | {
          heroImageUrl?: string | null;
          heroMediaKey?: string | null;
          trainings?: Array<{
            thumbnailUrl?: string | null;
            thumbnailMediaKey?: string | null;
            videoUrl?: string | null;
          }>;
        }
      | undefined;

    const mod = fromItems ?? after;
    if (!mod) return null;

    const heroData = lookupMediaDataUrl(preview.mediaBase64, mod.heroMediaKey);
    if (heroData) return heroData;
    if (mod.heroImageUrl) return mod.heroImageUrl;

    for (const t of mod.trainings ?? []) {
      const thumbData = lookupMediaDataUrl(
        preview.mediaBase64,
        t.thumbnailMediaKey,
      );
      if (thumbData) return thumbData;
      if (t.thumbnailUrl) return t.thumbnailUrl;
      if (t.videoUrl && isYoutubeUrl(t.videoUrl)) {
        const ytId = getYoutubeVideoId(t.videoUrl);
        if (ytId) return getYoutubeThumbnailUrl(ytId);
      }
    }
    return null;
  }

  if (preview.entity === "organizations") {
    const fromItems = (preview.items as Array<{
      slug?: string;
      logoUrl?: string | null;
    }>).find((item) => item.slug === change.matchKey);
    const after = change.after as { logoUrl?: string | null } | undefined;
    return fromItems?.logoUrl ?? after?.logoUrl ?? null;
  }

  if (preview.entity === "languages") {
    const fromItems = (
      preview.items as Array<{
        code?: string;
        flagUrl?: string | null;
        flagMediaKey?: string | null;
      }>
    ).find((item) => item.code === change.matchKey);
    const after = change.after as {
      flagUrl?: string | null;
      flagMediaKey?: string | null;
    } | undefined;
    const before = change.before as {
      flagUrl?: string | null;
      flagMediaKey?: string | null;
    } | undefined;
    const lang = fromItems ?? after ?? before;
    if (!lang) return null;
    const fromMedia = lookupMediaDataUrl(preview.mediaBase64, lang.flagMediaKey);
    if (fromMedia) return fromMedia;
    return lang.flagUrl ?? null;
  }

  return null;
}

function enrichChangeRows(preview: ImportPreviewResult): ChangeRow[] {
  return preview.changeset.items.map((item) => ({
    ...item,
    imageUrl: resolveChangeImageUrl(preview, item),
  }));
}

const columns: ColumnDefinition<ChangeRow>[] = [
  {
    id: "image",
    label: "",
    width: 56,
    enableHiding: false,
    pinnedLeft: true,
    render: (row) =>
      row.imageUrl ? (
        <div style={thumbCellStyle}>
          {/* Plain img — ImageLoader stays opacity:0 until load, which looks empty for failed CDN URLs */}
          <img
            src={row.imageUrl}
            alt=""
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />
        </div>
      ) : (
        <div style={thumbPlaceholderStyle}>
          <ImageIcon size={16} style={{ opacity: 0.4, color: vars.color.fgMuted }} />
        </div>
      ),
  },
  {
    id: "kind",
    label: "Type",
    sortable: true,
    render: (row) => (
      <span style={{ color: KIND_COLOR[row.kind], fontWeight: 600 }}>
        {KIND_LABEL[row.kind]}
      </span>
    ),
  },
  {
    id: "label",
    label: "Item",
    sortable: true,
    render: (row) => row.label,
  },
  {
    id: "matchKey",
    label: "Key",
    render: (row) => row.matchKey,
  },
  {
    id: "fields",
    label: "Changes",
    render: (row) => {
      const fields = row.fields ?? [];
      if (!fields.length) return "—";
      return (
        fields
          .slice(0, 3)
          .map((f) => f.field)
          .join(", ") + (fields.length > 3 ? ` +${fields.length - 3}` : "")
      );
    },
  },
];

export function ImportModal({
  open,
  onClose,
  expectedEntity,
  entityLabel,
  onApplied,
}: ImportModalProps) {
  const { t } = useI18n();
  const { accessToken } = useAuth();
  const token = accessToken ?? getAuthCookie("access_token") ?? "";
  const [preview, setPreview] = React.useState<ImportPreviewResult | null>(null);
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null);
  const [selectedIds, setSelectedIds] = React.useState<string[]>([]);
  const [phase, setPhase] = React.useState<ImportPhase>("idle");
  const [error, setError] = React.useState<string | null>(null);
  const [jobId, setJobId] = React.useState<string | null>(null);
  const [mediaExpanded, setMediaExpanded] = React.useState(true);
  const [localizationExpanded, setLocalizationExpanded] = React.useState(true);
  const fileRef = React.useRef<HTMLInputElement>(null);

  const clearFile = React.useCallback(() => {
    setPreview(null);
    setSelectedFile(null);
    setSelectedIds([]);
    setPhase("idle");
    setError(null);
    setJobId(null);
    setMediaExpanded(true);
    setLocalizationExpanded(false);
    if (fileRef.current) fileRef.current.value = "";
  }, []);

  React.useEffect(() => {
    if (!open) {
      clearFile();
    }
  }, [open, clearFile]);

  const onFile = async (file: File | null) => {
    if (!file || !token) return;
    setError(null);
    setPhase("parsing");
    setSelectedFile(file);
    try {
      const result = await previewImport(token, file);
      if (expectedEntity && result.entity !== expectedEntity) {
        throw new Error(
          `File is for ${result.entity}, but this list expects ${expectedEntity}`,
        );
      }
      const actionableIds = result.changeset.items
        .filter((i) => i.kind !== "unchanged")
        .map((i) => i.id);
      setPreview(result);
      setSelectedIds(actionableIds);
      setPhase("idle");
    } catch (e: any) {
      setError(e?.message || "Preview failed");
      setPreview(null);
      setSelectedFile(null);
      setPhase("error");
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const changeRows = React.useMemo(
    () => (preview ? enrichChangeRows(preview) : []),
    [preview],
  );

  const fetchList = React.useCallback(
    (params: ListParams): Promise<ListResult<ChangeRow>> => {
      const filtered = params.search
        ? changeRows.filter((i) =>
            [i.label, i.matchKey, KIND_LABEL[i.kind]].some((v) =>
              v.toLowerCase().includes(params.search!.toLowerCase()),
            ),
          )
        : changeRows;
      const start = (params.page - 1) * params.limit;
      return Promise.resolve({
        data: filtered.slice(start, start + params.limit),
        total: filtered.length,
      });
    },
    [changeRows],
  );

  const allIds = preview?.changeset.items.map((i) => i.id) ?? [];
  const actionableIds = React.useMemo(
    () =>
      (preview?.changeset.items ?? [])
        .filter((i) => i.kind !== "unchanged")
        .map((i) => i.id),
    [preview],
  );
  const unchangedCount = preview?.changeset.summary.unchanged ?? 0;
  const hasFile = Boolean(selectedFile && preview);
  const inProgress =
    phase === "parsing" ||
    phase === "starting" ||
    phase === "pending" ||
    phase === "running";
  const showProgress = phase !== "idle";
  const copy = progressCopy(phase, error);

  const selectedModules = React.useMemo(() => {
    if (!preview || preview.entity !== "certification-modules") return [];
    return selectedCertModules(preview, selectedIds);
  }, [preview, selectedIds]);

  const selectedLanguagePacks = React.useMemo(() => {
    if (!preview || preview.entity !== "languages") return [];
    return selectedLanguages(preview, selectedIds);
  }, [preview, selectedIds]);

  const scopedMediaKeys = React.useMemo(() => {
    if (preview?.entity === "languages") {
      return collectMediaKeysFromLanguages(selectedLanguagePacks);
    }
    return collectMediaKeysFromCertModules(selectedModules);
  }, [preview?.entity, selectedLanguagePacks, selectedModules]);

  const scopedMediaBase64 = React.useMemo(
    () => sliceMediaBase64(preview?.mediaBase64, scopedMediaKeys),
    [preview?.mediaBase64, scopedMediaKeys],
  );

  const mediaPreviewItems = React.useMemo(() => {
    if (preview?.entity === "languages") {
      return mediaEntriesFromSelectedLanguages(
        selectedLanguagePacks,
        // Prefer full preview map so basename aliases resolve for gallery tiles.
        preview.mediaBase64 ?? scopedMediaBase64,
      );
    }
    return mediaEntriesFromSelectedModules(selectedModules, scopedMediaBase64);
  }, [
    preview?.entity,
    preview?.mediaBase64,
    selectedLanguagePacks,
    selectedModules,
    scopedMediaBase64,
  ]);

  const mediaKeyCount = mediaPreviewItems.length;
  const omittedLargeCount = Math.max(
    0,
    scopedMediaKeys.filter((k) => !scopedMediaBase64[k]).length,
  );
  const showMediaSection = mediaKeyCount > 0 || omittedLargeCount > 0;

  const scopedLocalization = React.useMemo(() => {
    if (!preview?.localization) return null;
    const contentKeys = collectContentKeysFromCertModules(selectedModules);
    return sliceLocalizationForKeys(preview.localization, contentKeys);
  }, [preview?.localization, selectedModules]);

  const showLocalizationSection = Boolean(scopedLocalization);
  const localizationSubtitle = React.useMemo(() => {
    if (!scopedLocalization) return undefined;
    const langs = Object.keys(scopedLocalization.definitionsByLang).length;
    const keys = new Set<string>();
    for (const defs of Object.values(scopedLocalization.definitionsByLang)) {
      for (const k of Object.keys(defs)) keys.add(k);
    }
    return `${langs} language${langs === 1 ? "" : "s"} · ${keys.size} key${keys.size === 1 ? "" : "s"}`;
  }, [scopedLocalization]);

  const languageDefinitionsPreview = React.useMemo(() => {
    if (!preview || preview.entity !== "languages") return null;
    return buildLanguageDefinitionsPreview(preview, selectedIds);
  }, [preview, selectedIds]);

  const showDefinitionsSection = Boolean(languageDefinitionsPreview);
  const definitionsSubtitle = React.useMemo(() => {
    if (!languageDefinitionsPreview) return undefined;
    const langs = Object.keys(languageDefinitionsPreview.languages).length;
    let keys = 0;
    for (const pack of Object.values(languageDefinitionsPreview.languages)) {
      keys += pack.summary.totalKeys;
    }
    return `${langs} language${langs === 1 ? "" : "s"} · ${keys} key change${keys === 1 ? "" : "s"}`;
  }, [languageDefinitionsPreview]);

  const noopMediaChange = React.useCallback((_items: MediaEntry[]) => {}, []);
  const noopUpload = React.useCallback(async () => ({ url: "" }), []);

  const selectionSummary = preview
    ? [
        `${selectedIds.length} of ${actionableIds.length} change${actionableIds.length === 1 ? "" : "s"} selected`,
        unchangedCount > 0
          ? `${unchangedCount} unchanged`
          : null,
        "A checkpoint will be created before apply.",
      ]
        .filter(Boolean)
        .join(". ")
    : "";

  const getRowCanSelect = React.useCallback(
    (row: ChangeRow) => row.kind !== "unchanged",
    [],
  );

  const onSelectionChange = React.useCallback((ids: string[]) => {
    setSelectedIds(ids.filter((id) => !id.startsWith("nop:")));
  }, []);

  const onApply = async () => {
    if (!token || !preview || !selectedIds.length) return;
    setError(null);
    setPhase("starting");
    setJobId(null);
    try {
      const { jobId: id } = await applyImport(token, {
        entity: preview.entity,
        changeIds: selectedIds,
        items: preview.items,
        enumGroups: preview.enumGroups,
        // Language flags are small; send the full ZIP media map so key aliases
        // from older packs still resolve on the server.
        mediaBase64:
          preview.entity === "languages"
            ? preview.mediaBase64 ?? scopedMediaBase64
            : scopedMediaBase64,
        previewId: preview.previewId ?? undefined,
        localization: scopedLocalization,
      });
      setJobId(id);
      setPhase("pending");
      const job = await waitForJob(token, id, {
        onUpdate: (j) => {
          const next = phaseFromJobStatus(j.status);
          if (next && next !== "done" && next !== "error") setPhase(next);
          if (j.status === "failed") {
            setError(j.error || "Import failed");
          }
        },
      });
      if (job.status === "failed") {
        setError(job.error || "Import failed");
        setPhase("error");
        return;
      }
      setPhase("done");
      onApplied?.();
    } catch (e: any) {
      setError(e?.message || "Import failed");
      setPhase("error");
    }
  };

  return (
    <Modal
      open={open}
      setOpen={(v) => {
        if (!v) onClose();
      }}
      minWidth="720px"
      maxWidth="960px"
      maxHeight="90vh"
      closeOnClickOff={!inProgress}
    >
      <Modal.Header>
        <Text level="title-small">Import {entityLabel}</Text>
      </Modal.Header>
      <Modal.Body>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 12,
            minHeight: 320,
          }}
        >
          <input
            ref={fileRef}
            type="file"
            accept=".json,.zip,.csv"
            style={{ display: "none" }}
            onChange={(e) => void onFile(e.target.files?.[0] ?? null)}
          />

          {!showProgress ? (
            !hasFile ? (
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 12,
                  textAlign: "center",
                  padding: "32px 16px",
                }}
              >
                <Button
                  variant="outline"
                  iconLeft={<Download size={16} />}
                  onClick={() => fileRef.current?.click()}
                >
                  Choose file
                </Button>
                <Text level="body-small" color="muted">
                  JSON or ZIP.
                </Text>
              </div>
            ) : (
              <>
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    alignSelf: "flex-start",
                    maxWidth: "100%",
                    padding: "4px 6px 4px 10px",
                    borderRadius: 9999,
                    background: vars.color.muted,
                    border: `1px solid ${vars.color.border}`,
                  }}
                >
                  <File
                    size={14}
                    strokeWidth={2}
                    style={{ color: vars.color.fgMuted, flexShrink: 0 }}
                  />
                  <Text
                    level="body-small"
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: 360,
                    }}
                  >
                    {selectedFile!.name}
                  </Text>
                  <button
                    type="button"
                    aria-label={t("pro.dataTransfer.removeFile")}
                    onClick={clearFile}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 24,
                      height: 24,
                      border: "none",
                      borderRadius: 9999,
                      background: "transparent",
                      color: vars.color.fgMuted,
                      cursor: "pointer",
                      flexShrink: 0,
                      padding: 0,
                    }}
                  >
                    <X size={14} />
                  </button>
                </div>

                <div style={{ flex: 1, minHeight: 240 }}>
                  <ListView<ChangeRow>
                    listId={`import-changeset-${preview!.entity}`}
                    columns={columns}
                    fetchList={fetchList}
                    enabledViews={["table"]}
                    enableSelection
                    getRowCanSelect={getRowCanSelect}
                    defaultSelectedIds={actionableIds}
                    onSelectionChange={onSelectionChange}
                    disableLocalPersistence
                    contextKey={`${preview!.entity}:${allIds.length}:${selectedFile!.name}:${changeRows.filter((r) => r.imageUrl).length}`}
                    queryOptions={{ staleTime: Infinity }}
                    emptyTitle="No changes"
                    emptySubtitle={t("pro.dataTransfer.fileMatchesCurrent")}
                    defaultPageSize={10}
                  />
                  <div style={{ marginTop: 10 }}>
                    <Callout variant="info">{selectionSummary}</Callout>
                  </div>
                </div>

                <ChangeSummaryChips summary={preview!.changeset.summary} />

                {showMediaSection && (
                  <CollapsibleSection
                    id="import-preview-media"
                    title={t("pro.dataTransfer.media")}
                    subtitle={`${mediaKeyCount} file${mediaKeyCount === 1 ? "" : "s"}`}
                    icon={<Images size={16} />}
                    expanded={mediaExpanded}
                    onToggleExpanded={() => setMediaExpanded((v) => !v)}
                    compactHeader
                  >
                    {mediaPreviewItems.length === 0 ? (
                      <Text level="body-small" color="muted">
                        Media will be imported with the selected changes.
                        Large files may be omitted from this preview.
                      </Text>
                    ) : (
                      <>
                        <MediaUploader
                          mediaType={
                            preview!.entity === "languages" ? "image" : "video"
                          }
                          items={mediaPreviewItems}
                          onChange={noopMediaChange}
                          onApply={noopMediaChange}
                          uploadFile={noopUpload}
                          maxItems={Math.max(mediaPreviewItems.length, 2)}
                          maxRows={2}
                          readOnly
                          selectable={false}
                          pasteEnabled={false}
                          renderBadge={renderMediaTypeBadge}
                        />
                        {omittedLargeCount > 0 ? (
                          <Text
                            level="body-small"
                            color="muted"
                            style={{ marginTop: 8, display: "block" }}
                          >
                            {omittedLargeCount} large file
                            {omittedLargeCount === 1 ? "" : "s"} omitted from
                            preview but included on apply.
                          </Text>
                        ) : null}
                      </>
                    )}
                  </CollapsibleSection>
                )}

                {showLocalizationSection && scopedLocalization && (
                  <CollapsibleSection
                    id="import-preview-localization"
                    title={t("pro.system.localization.title")}
                    subtitle={localizationSubtitle}
                    icon={<Globe size={16} />}
                    expanded={localizationExpanded}
                    onToggleExpanded={() =>
                      setLocalizationExpanded((v) => !v)
                    }
                    compactHeader
                  >
                    <div style={jsonPreviewShellStyle}>
                      <JsonView
                        data={scopedLocalization}
                        style={darkStyles}
                        shouldExpandNode={(level: number) => level < 2}
                      />
                    </div>
                  </CollapsibleSection>
                )}

                {showDefinitionsSection && languageDefinitionsPreview && (
                  <CollapsibleSection
                    id="import-preview-definitions"
                    title={t("pro.system.localization.definitions")}
                    subtitle={definitionsSubtitle}
                    icon={<Globe size={16} />}
                    expanded={localizationExpanded}
                    onToggleExpanded={() =>
                      setLocalizationExpanded((v) => !v)
                    }
                    compactHeader
                  >
                    <div style={jsonPreviewShellStyle}>
                      <JsonView
                        data={languageDefinitionsPreview}
                        style={darkStyles}
                        shouldExpandNode={(level: number) => level < 3}
                      />
                    </div>
                  </CollapsibleSection>
                )}
              </>
            )
          ) : copy ? (
            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                gap: 12,
                padding: "28px 12px 16px",
              }}
            >
              <Text
                level="title-small"
                weight="medium"
                color={phase === "error" ? "danger" : undefined}
              >
                {copy.title}
              </Text>
              <copy.Icon
                size={36}
                strokeWidth={1.75}
                style={{
                  color: copy.iconColor,
                  flexShrink: 0,
                  ...(copy.spin
                    ? { animation: "import-spin 0.9s linear infinite" }
                    : null),
                }}
              />
              {copy.subtitle && (
                <Text level="body-small" color="muted">
                  {copy.subtitle}
                </Text>
              )}
            </div>
          ) : null}
        </div>
        {showProgress &&
          (phase === "parsing" || phase === "starting") && (
            <style>{`@keyframes import-spin { to { transform: rotate(360deg); } }`}</style>
          )}
      </Modal.Body>
      <Modal.Footer style={{ justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", minWidth: 28 }}>
          {showProgress && jobId ? (
            <span
              title={`Job ${jobId}`}
              aria-label={`Job ${jobId}`}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                color: vars.color.fgMuted,
                cursor: "default",
              }}
            >
              <Info size={16} strokeWidth={2} />
            </span>
          ) : null}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Button variant="ghost" color="neutral" onClick={onClose}>
            {showProgress ? "Close" : "Cancel"}
          </Button>
          {phase === "error" ? (
            hasFile ? (
              <Button
                color="primary"
                onClick={onApply}
                iconLeft={<Upload size={16} />}
              >
                Try again
              </Button>
            ) : (
              <Button
                color="primary"
                onClick={() => {
                  setPhase("idle");
                  setError(null);
                  fileRef.current?.click();
                }}
                iconLeft={<Download size={16} />}
              >
                Choose file
              </Button>
            )
          ) : phase === "done" ? null : (
            <Button
              color="primary"
              onClick={onApply}
              disabled={inProgress || !preview || selectedIds.length === 0}
              iconLeft={<Upload size={16} />}
            >
              {inProgress
                ? "Applying…"
                : `Apply ${selectedIds.length} change${selectedIds.length === 1 ? "" : "s"}`}
            </Button>
          )}
        </div>
      </Modal.Footer>
    </Modal>
  );
}
